import React, { useState } from 'react'
import { Button, Modal } from '@surecompanies/core_components'
import { WarningOutlined } from '@ant-design/icons'
import isNil from 'lodash/isNil'
import { RequiredMark } from '../shared/finance.styles'
import {
  GrantProgress,
  GrantRow,
  GrantRowDetail,
  GrantRowEmail,
  GrantRowList,
  GrantSection,
  GrantSectionHeader,
  GrantSectionHint,
  GrantSectionTitle,
  GrantScroll,
  InviteModalForm,
  ReasonField,
  ReasonLabel,
  ReasonTextarea,
  WarningNote,
} from './UserManagement.styles'
import { AccessResultList } from './AccessResultList'
import { isHrAdminRbacRoleName, joinNames, pluralize } from './userDisplay'
import {
  RemoveFundingAccessAction,
  UserDisplayStatus,
} from '../../api/dto/user-invitation.dto'
import type { AccessOutcomeStyle } from './AccessResultList'
import type {
  AccessRemovalPlan,
  AccessRemovalTarget,
} from './accessRemovalBuckets'
import type { RemoveFundingAccessResultItem } from '../../api/dto/user-invitation.dto'

const REASON_FIELD_ID = 'remove-access-reason'

interface RemoveAccessModalProps {
  open: boolean
  plan: AccessRemovalPlan
  confirmLoading: boolean
  /** Per-email outcomes once the run finishes. Null while on the confirm step. */
  results: RemoveFundingAccessResultItem[] | null
  progress: { processed: number; total: number } | null
  onClose: () => void
  onSubmit: (reason: string) => void
}

const REMOVAL_OUTCOMES: AccessOutcomeStyle<RemoveFundingAccessAction>[] = [
  {
    action: RemoveFundingAccessAction.Removed,
    label: 'Removed',
    summaryLabel: 'removed',
    tone: 'success',
  },
  {
    action: RemoveFundingAccessAction.Skipped,
    label: 'Skipped',
    summaryLabel: 'skipped',
    tone: 'notice',
  },
  {
    action: RemoveFundingAccessAction.Failed,
    label: 'Failed',
    summaryLabel: 'failed',
    tone: 'error',
  },
]

const companies = (count: number) =>
  `${count} ${count === 1 ? 'company' : 'companies'}`

/**
 * The line under each person. Their company list is never edited here: every
 * role shares it and nothing records which role a company was added for, so we
 * cannot tell which ones an HR Admin should keep. My People edits it.
 */
const describeRemoval = ({
  row,
  roles,
  hasLiveInvitation,
}: AccessRemovalTarget) => {
  const isInvoiced = row.billingClientNames.length > 0
  const isHrAdmin =
    row.displayStatus === UserDisplayStatus.Active &&
    (row.rbacRoles ?? []).some((role) => isHrAdminRbacRoleName(role.name))

  if (isHrAdmin) {
    const keeps = isInvoiced
      ? `They keep HR Admin access to ${joinNames(row.billingClientNames)}.`
      : 'They keep HR Admin access.'
    return isInvoiced ? `They'll stop getting invoices. ${keeps}` : keeps
  }
  if (roles.length > 0) {
    return isInvoiced
      ? "They'll stop getting invoices and lose portal access."
      : "They'll lose portal access."
  }
  if (hasLiveInvitation) {
    return isInvoiced
      ? "They'll stop getting invoices, and their sign-up link stops working."
      : 'Their sign-up link stops working.'
  }
  return "They'll stop getting invoices."
}

/** What the run actually did, so a success row is not left blank. */
const describeOutcome = (result: RemoveFundingAccessResultItem) => {
  if (result.reason) return result.reason
  const parts = [
    result.invitationRevoked ? 'invitation withdrawn' : null,
    result.rolesRemoved ? 'role removed' : null,
    result.profilesRemoved > 0
      ? `invoices stopped for ${companies(result.profilesRemoved)}`
      : null,
  ].filter((part) => !isNil(part))
  return parts.join(' · ')
}

/**
 * Confirms and reports taking funding access away in full. Every listed user
 * holds the role or receives invoices, so there is always something to remove
 * and nothing to skip at this step; what differs per row is how much goes with
 * it.
 */
const RemoveAccessModal = ({
  open,
  plan,
  confirmLoading,
  results,
  progress,
  onClose,
  onSubmit,
}: RemoveAccessModalProps) => {
  const { targets, invitationCount, roleCount } = plan
  const isDone = !isNil(results)
  const [reason, setReason] = useState('')
  const canSubmit = targets.length > 0 && reason.trim().length > 0

  const handleClose = () => {
    setReason('')
    onClose()
  }

  const renderConfirm = () => (
    <InviteModalForm>
      <GrantSection>
        <GrantSectionHeader>
          <GrantSectionTitle>
            Will lose Finance Manager access ({targets.length})
          </GrantSectionTitle>
          <GrantSectionHint>Takes effect right away.</GrantSectionHint>
        </GrantSectionHeader>
        <GrantScroll>
          <GrantRowList>
            {targets.map((target) => (
              <GrantRow key={target.row.email}>
                <GrantRowEmail>{target.row.email}</GrantRowEmail>
                <GrantRowDetail>{describeRemoval(target)}</GrantRowDetail>
              </GrantRow>
            ))}
          </GrantRowList>
        </GrantScroll>
        <GrantSectionHint>
          {invitationCount > 0
            ? `${pluralize(invitationCount, 'sign-up link')} ${
                invitationCount === 1 ? 'stops' : 'stop'
              } working. `
            : ''}
          {roleCount > 0
            ? `${pluralize(roleCount, 'user')} ${
                roleCount === 1 ? 'loses' : 'lose'
              } the Finance Manager role.`
            : ''}
        </GrantSectionHint>
      </GrantSection>

      <ReasonField>
        <ReasonLabel htmlFor={REASON_FIELD_ID}>
          Reason <RequiredMark>*</RequiredMark>
        </ReasonLabel>
        <ReasonTextarea
          id={REASON_FIELD_ID}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
          placeholder="Explain why this action is being taken…"
        />
        <WarningNote>
          <WarningOutlined />
          <span>
            A company left with no recipients stops receiving invoices
            altogether. If HubSpot still lists one of these addresses, the next
            sync offers to add it back.
          </span>
        </WarningNote>
      </ReasonField>

      {progress ? (
        <GrantProgress aria-live="polite">
          Processed {progress.processed} of {progress.total}…
        </GrantProgress>
      ) : null}
    </InviteModalForm>
  )

  return (
    <Modal
      title={
        isDone
          ? 'Remove Finance Manager results'
          : 'Remove Finance Manager access'
      }
      open={open}
      onOk={canSubmit ? () => onSubmit(reason.trim()) : undefined}
      onCancel={handleClose}
      okText={
        targets.length > 0
          ? `Remove access from ${pluralize(targets.length, 'user')}`
          : 'Remove access'
      }
      confirmLoading={confirmLoading}
      okButtonProps={{ danger: true, disabled: !canSubmit }}
      cancelButtonProps={{ disabled: confirmLoading }}
      footer={
        isDone
          ? [
              <Button key="done" type="primary" onClick={handleClose}>
                Done
              </Button>,
            ]
          : undefined
      }
      width={640}
      style={{ maxWidth: 'calc(100vw - 32px)' }}
      destroyOnClose
      // Same reason as GrantAccessModal: the report dies with the modal, so a
      // run in flight has to land before any close route works.
      closable={!confirmLoading}
      maskClosable={!confirmLoading}
      keyboard={!confirmLoading}
    >
      {isDone ? (
        <AccessResultList
          items={(results ?? []).map((result) => ({
            email: result.email,
            action: result.action,
            reason: describeOutcome(result),
          }))}
          outcomes={REMOVAL_OUTCOMES}
        />
      ) : (
        renderConfirm()
      )}
    </Modal>
  )
}

export { RemoveAccessModal }
